import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import { App, Plan, Subscription } from "../types/entities";

export type ToolPrimaryAction = "open" | "activate" | "select-plan";

export type ToolBadgeTone = "trial" | "active" | "expired";

export interface ToolStatusDisplay {
  badge: string;
  badgeTone: ToolBadgeTone;
  context: string;
  primaryAction: ToolPrimaryAction;
}

export const BADGE_PALETTE: Record<
  ToolBadgeTone,
  { bg: string; color: string }
> = {
  trial: { bg: "#fff4d6", color: "#8a6d00" },
  active: { bg: "#dff5e1", color: "#2e7d32" },
  expired: { bg: "#ffe6cc", color: "#b65a00" },
};

function daysUntil(dateString: string): number {
  const target = new Date(dateString).getTime();
  const now = Date.now();
  return Math.max(0, Math.ceil((target - now) / (1000 * 60 * 60 * 24)));
}

export function getToolStatusDisplay(
  app: App,
  subscription: Subscription | null | undefined,
  currentPlan: Plan | null | undefined,
  paidToolPriceEur: number,
  /** All apps for the client — used to compute the live included-slot count (B10 fix). */
  allApps: App[] = [],
  langauge: SupportedLanguage
): ToolStatusDisplay {
  const status = app.activationStatus ?? "trial";
  const planActive = !!subscription;
  const includedTotal = currentPlan?.includedTools ?? 0;
  const includedUsed = allApps.filter(
    (a) => a.activationStatus === "active_included"
  ).length;

  switch (status) {
    case "trial": {
      const daysLeft = app.trial?.expiresAt
        ? daysUntil(app.trial.expiresAt)
        : null;
      return {
        badge: getLocalizedMessage(langauge, "appStatus.trial"),
        badgeTone: "trial",
        context:
          daysLeft != null
            ? getLocalizedMessage(
                langauge,
                daysLeft === 1 ? "appStatus.oneDayLeft" : "appStatus.daysLeft",
                { days: daysLeft }
              )
            : getLocalizedMessage(langauge, "appStatus.trial"),
        primaryAction: "open",
      };
    }
    case "active_included":
      return {
        badge: getLocalizedMessage(langauge, "appStatus.active"),
        badgeTone: "active",
        context: getLocalizedMessage(
          langauge,
          "appStatus.includedUsersOfTotal",
          { included: includedUsed, total: Math.max(includedTotal, 1) }
        ),
        primaryAction: "open",
      };
    case "active_paid":
      return {
        badge: getLocalizedMessage(langauge, "appStatus.active"),
        badgeTone: "active",
        context: getLocalizedMessage(langauge, "appStatus.pricePerMonth", {
          price: paidToolPriceEur,
        }),
        primaryAction: "open",
      };
    case "expired":
    default:
      return {
        badge: getLocalizedMessage(langauge, "appStatus.expired"),
        badgeTone: "expired",
        context: getLocalizedMessage(
          langauge,
          planActive ? "appStatus.requiresActivation" : "appStatus.requiresPlan"
        ),
        primaryAction: planActive ? "activate" : "select-plan",
      };
  }
}
